import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { currentUserId } from "@/lib/currentUserId";
import axiosInstance from "@/lib/axios";
import { notificationEndpoint } from "@/config/endpoints";
import {
  clearNotifications,
  markNotificationsRead,
  prependNotification,
  removeNotification,
  toServerNotification,
  type NotificationListData,
  type NotificationListFilter,
  type NotificationPush,
} from "./notificationCachePatch";

// ─── Query Keys ────────────────────────────────────────────
/**
 * The bell dropdown's list ONLY. The two shuffle modals used to sit under
 * this prefix too (`["notifications", "shuffle-warnings"]`, …) so every
 * socket push refetched all three — see `SHUFFLE_WARNINGS_KEY` /
 * `SHUFFLE_LOGIN_SUMMARY_KEY` below, which are deliberately NOT prefixed.
 */
const NOTIFICATION_KEY = ["notifications"] as const;

/** Pre-shuffle warning cards (`ShuffleWarningModal`). Socket-refetched only
 *  when the pushed row IS a warning. */
export const SHUFFLE_WARNINGS_KEY = ["shuffle-warnings"] as const;
/** Login-time shuffle summary (`ShuffleSummaryModal`). Fetched once per app
 *  boot; nothing invalidates it. */
export const SHUFFLE_LOGIN_SUMMARY_KEY = ["shuffle-login-summary"] as const;

export const NOTIFICATION_STALE_MS = 3 * 60 * 1000;

export interface NotificationFilters {
  unreadOnly?: boolean;
  type?: string;
  /** Stage / Status / Sub-Status names to match payload.fromValue/toValue. */
  values?: string[];
}

function notificationKeys(filters?: NotificationFilters) {
  // Include sorted values in the key so changing the filter creates a new
  // cache entry and triggers a fresh fetch (rather than re-displaying the
  // previous filter's pages).
  const sortedValues = filters?.values ? [...filters.values].sort() : undefined;
  return [
    ...NOTIFICATION_KEY,
    {
      unreadOnly: filters?.unreadOnly ?? false,
      type: filters?.type ?? null,
      values: sortedValues ?? null,
    } satisfies NotificationListFilter,
  ] as const;
}

// ─── Types ─────────────────────────────────────────────────
type NotificationListResponse = NotificationListData["pages"][number];

// ─── List (infinite scroll, cursor-based) ──────────────────
export function useNotifications(filters?: NotificationFilters) {
  return useInfiniteQuery<NotificationListResponse>({
    queryKey: notificationKeys(filters),
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string> = { limit: "20" };
      if (pageParam) params.cursor = pageParam as string;
      if (filters?.unreadOnly) params.unreadOnly = "true";
      if (filters?.type) params.type = filters.type;
      if (filters?.values && filters.values.length > 0) {
        params.values = filters.values.join(",");
      }

      const { data } = await axiosInstance.get(notificationEndpoint, { params });
      return data;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    staleTime: NOTIFICATION_STALE_MS,
  });
}

// ─── Helpers: flattened data + unreadCount ──────────────────
export function useNotificationData(filters?: NotificationFilters) {
  const query = useNotifications(filters);
  const notifications = query.data?.pages.flatMap((p) => p.data) ?? [];
  const unreadCount = query.data?.pages[0]?.unreadCount ?? 0;
  return { ...query, notifications, unreadCount };
}

// ─── In-place cache patching ───────────────────────────────
/**
 * Every list variant currently cached (one per filter pill the user has
 * visited). The filter object rides at `queryKey[1]`; the modals' keys no
 * longer share the prefix, so this is the dropdown and nothing else.
 */
function forEachListQuery(
  patch: (
    data: NotificationListData | undefined,
    filter: NotificationListFilter | undefined,
  ) => NotificationListData | undefined,
) {
  const queries = queryClient.getQueryCache().findAll({ queryKey: NOTIFICATION_KEY });
  for (const q of queries) {
    const filter = q.queryKey[1] as NotificationListFilter | undefined;
    queryClient.setQueryData<NotificationListData>(q.queryKey, (old) =>
      patch(old, filter),
    );
  }
}

/** The dropdown reports whether its popover is open (see `setNotificationDropdownOpen`). */
let dropdownOpen = false;
let openRefetchTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Refetch the ACTIVE list — only ever while the dropdown is open, and
 * coalesced: a bulk sweep's burst of pushes while the user is reading the
 * list becomes one GET, not one per push. The cache patch has already put
 * the row on screen; this just reconciles server-side ordering / filtering.
 */
function refetchOpenListSoon() {
  if (!dropdownOpen || openRefetchTimer) return;
  openRefetchTimer = setTimeout(() => {
    openRefetchTimer = null;
    if (!dropdownOpen) return;
    queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEY, refetchType: "active" });
  }, 1500);
}

/**
 * Called by `NotificationDropdown` on every open/close. Opening after the
 * list has gone stale (3 min) refetches it once — the socket patches keep
 * the rows faithful in between, so this is a safety net, not the feed.
 */
export function setNotificationDropdownOpen(open: boolean) {
  dropdownOpen = open;
  if (!open) return;
  const stale = queryClient
    .getQueryCache()
    .findAll({ queryKey: NOTIFICATION_KEY, type: "active" })
    .some((q) => q.isStaleByTime(NOTIFICATION_STALE_MS));
  if (stale) {
    queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEY, refetchType: "active" });
  }
}

/**
 * Socket `system:notification` → cache. Never a GET: the badge flips on
 * the push itself, on every pm2 worker's fan-out alike.
 */
export function applyNotificationPush(push: NotificationPush | null | undefined) {
  if (!push || typeof push !== "object" || !("action" in push)) return;
  switch (push.action) {
    case "created": {
      const n = push.notification;
      if (!n || n.id == null) return;
      const row = toServerNotification(n, { userId: currentUserId() ?? 0, tenantId: 0 });
      forEachListQuery((old, filter) =>
        prependNotification(old, row, filter, push.unreadCount ?? null),
      );
      refetchOpenListSoon();
      return;
    }
    case "read": {
      const ids = push.notificationId == null ? null : [push.notificationId];
      forEachListQuery((old, filter) =>
        markNotificationsRead(old, ids, push.unreadCount ?? null, filter),
      );
      return;
    }
    case "dismissed": {
      if (push.notificationId == null) {
        forEachListQuery((old) => clearNotifications(old));
      } else {
        const id = push.notificationId;
        forEachListQuery((old) => removeNotification(old, id));
      }
      return;
    }
    case "cleared": {
      forEachListQuery((old) => clearNotifications(old));
      return;
    }
    default:
      return;
  }
}

/**
 * Mark rows read in the dropdown cache without a round-trip — used by the
 * shuffle modals after their own PATCH `/read` calls (the server's `read`
 * push re-applies the same patch; idempotent).
 */
export function patchNotificationsRead(ids: number[], unreadCount: number | null = null) {
  if (ids.length === 0) return;
  forEachListQuery((old, filter) => markNotificationsRead(old, ids, unreadCount, filter));
}

// ─── Mutations ─────────────────────────────────────────────
// Each mutation patches the cache from its own response (the server returns
// the recipient's unread count on the read paths). No invalidation: the
// old `invalidateQueries(NOTIFICATION_KEY)` cost a list GET per click on
// top of the socket echo the server sends the same user anyway.
export function useMarkRead() {
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await axiosInstance.patch(`${notificationEndpoint}/${id}/read`);
      return data as { unreadCount?: number };
    },
    onSuccess: (data, id) => {
      forEachListQuery((old, filter) =>
        markNotificationsRead(old, [id], data?.unreadCount ?? null, filter),
      );
    },
  });
}

export function useMarkAllRead() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.patch(`${notificationEndpoint}/read-all`);
      return data as { unreadCount?: number };
    },
    onSuccess: (data) => {
      forEachListQuery((old, filter) =>
        markNotificationsRead(old, null, data?.unreadCount ?? 0, filter),
      );
    },
  });
}

export function useDismissNotification() {
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await axiosInstance.patch(`${notificationEndpoint}/${id}/dismiss`);
      return data;
    },
    onSuccess: (_data, id) => {
      forEachListQuery((old) => removeNotification(old, id));
    },
  });
}

export function useClearAllNotifications() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.delete(`${notificationEndpoint}/clear`);
      return data;
    },
    onSuccess: () => {
      forEachListQuery((old) => clearNotifications(old));
    },
  });
}
